import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import TeamMemberView from '../components/TeamMemberView';
import MD3Switch from '../components/MD3Switch';
import InlineFieldEditor from '../components/InlineFieldEditor';
import TeamProfileEditor from '../components/TeamProfileEditor';
import UnifiedLoader from '../components/UnifiedLoader';

// Icons
import {
    PersonIcon,
    GlobeIcon,
    SaveIcon,
    SettingsIcon,
    HomeIcon,
    ExternalLinkIcon,
    SparklesIcon,
    CheckCircleIcon,
    AlertCircleIcon,
    ViewQuiltIcon,
    VisibilityIcon,
    ArrowLeftIcon
} from '../components/Icons';

const MAIN_URL = process.env.REACT_APP_MAIN_URL || 'https://pfadfinder-wutoeschingen.org';

const EditableTeamPage = () => {
    const { id } = useParams();
    const { user, API_URL } = useContext(AuthContext);
    const navigate = useNavigate();

    const [targetUser, setTargetUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [activeTab, setActiveTab] = useState('preview'); // 'preview' | 'blocks' | 'settings'

    // Local profile data state for editing
    const [localUserData, setLocalUserData] = useState({});
    const [blocks, setBlocks] = useState([]);
    const [visibilitySettings, setVisibilitySettings] = useState({});
    const [isVisible, setIsVisible] = useState(false);
    const [isFeaturedOnHomepage, setIsFeaturedOnHomepage] = useState(false);

    // UI State for inline editing popover
    const [editingField, setEditingField] = useState(null);

    useEffect(() => {
        const fetchMember = async () => {
            if (!id) return;
            setLoading(true);
            setError(null);
            try {
                const config = { headers: { Authorization: `Bearer ${user?.token}` } };
                // Fetch full user record (supports invisible / inactive profiles for admins)
                const res = await axios.get(`${API_URL}/users/${id}`, config);
                const u = res.data;
                setTargetUser(u);

                const ud = u.userData || {};
                setLocalUserData({
                    scoutName: ud.scoutName || '',
                    job: ud.job || '',
                    birthDate: ud.birthDate || '',
                    shortDescription: ud.shortDescription || '',
                    description: ud.description || '',
                    phone: ud.phone || '',
                    photos: ud.photos || []
                });
                setBlocks(ud.profileBlocks || []);
                setVisibilitySettings(ud.visibilitySettings || {});
                setIsVisible(ud.isVisible ?? false);
                setIsFeaturedOnHomepage(ud.isFeaturedOnHomepage ?? false);
            } catch (err) {
                console.error('Failed to load user by ID, trying fallback team endpoint:', err);
                try {
                    const fallbackUrl = `${process.env.REACT_APP_API_URL || API_URL.replace('/api', '')}/api/team/${id}`;
                    const fbRes = await axios.get(fallbackUrl);
                    const d = fbRes.data || {};
                    setTargetUser(d);
                    setLocalUserData({
                        scoutName: d.name || '',
                        job: d.job || d.role || '',
                        shortDescription: d.shortDescription || '',
                        description: d.description || '',
                        phone: d.phone || '',
                        photos: d.photos || []
                    });
                    setBlocks(d.profileBlocks || []);
                    setIsVisible(true);
                } catch (fallbackErr) {
                    setError(err.response?.data?.message || 'Profil konnte nicht geladen werden.');
                }
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchMember();
        }
    }, [id, API_URL, user]);

    const handleToggleVisibility = (field, value) => {
        setVisibilitySettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        if (!targetUser) return;
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const userId = targetUser._id || id;

            const updatePayload = {
                userData: {
                    ...(targetUser.userData || {}),
                    ...localUserData,
                    profileBlocks: blocks.map(({ _isNew, ...b }) => b),
                    isVisible,
                    isFeaturedOnHomepage,
                    visibilitySettings
                }
            };

            await axios.put(`${API_URL}/users/${userId}/profile`, updatePayload, config);

            // Update local state
            setTargetUser(prev => ({
                ...prev,
                userData: {
                    ...(prev?.userData || {}),
                    ...updatePayload.userData
                }
            }));

            setMessage({ type: 'success', text: 'Änderungen erfolgreich gespeichert!' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        } catch (err) {
            console.error('Save failed:', err);
            setMessage({ type: 'error', text: err.response?.data?.message || 'Fehler beim Speichern.' });
        } finally {
            setSaving(false);
        }
    };

    const handleEditField = (name, value) => {
        const fieldConfigs = {
            scoutName: { label: 'Pfadiname / Anzeigename', type: 'text', placeholder: 'Name in der Gruppe' },
            job: { label: 'Aufgabe / Rolle', type: 'text', placeholder: 'z.B. Gruppenleiter, Materialwart...' },
            shortDescription: { label: 'Kurzbeschreibung', type: 'text', placeholder: 'Ein kurzer Satz', maxLength: 150 },
            description: { label: 'Beschreibung', type: 'textarea', placeholder: 'Erzähle etwas über die Pfadfinderzeit...' },
            birthDate: { label: 'Geburtsdatum', type: 'date' },
            gallery: { label: 'Fotogalerie', type: 'special' }
        };

        if (name === 'gallery') {
            setActiveTab('blocks');
            setMessage({ type: 'info', text: 'Nutze den Bereich "Zusatz-Inhalte", um Fotos & Galerien zu verwalten.' });
            return;
        }

        setEditingField({
            name,
            value: value || '',
            ...fieldConfigs[name]
        });
    };

    const handleSaveInline = (newValue) => {
        setLocalUserData(prev => ({
            ...prev,
            [editingField.name]: newValue
        }));
        setEditingField(null);
    };

    if (loading) return <UnifiedLoader fullscreen />;

    if (error) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center p-6">
                <div className="bg-error-container text-error-onContainer p-8 rounded-3xl max-w-md text-center shadow-lg border border-error/20">
                    <AlertCircleIcon size={48} className="mx-auto mb-4 text-error" />
                    <h3 className="font-bold text-title-medium mb-2">Fehler beim Laden</h3>
                    <p className="text-body-medium mb-6 opacity-90">{error}</p>
                    <button onClick={() => navigate('/users')} className="md3-btn-filled w-full">
                        Zurück zur Übersicht
                    </button>
                </div>
            </div>
        );
    }

    if (!targetUser) return null;

    const displayName = localUserData.scoutName || targetUser.userData?.firstName || targetUser.name || targetUser.email || 'Teammitglied';
    const publicProfileUrl = targetUser._id ? `${MAIN_URL}/team/${targetUser._id}` : null;

    // Construct preview data
    const previewData = {
        ...targetUser,
        name: displayName,
        job: localUserData.job,
        shortDescription: localUserData.shortDescription,
        description: localUserData.description,
        email: targetUser.email,
        phone: localUserData.phone,
        photos: localUserData.photos || [],
        profileBlocks: blocks || [],
        age: localUserData.birthDate ? Math.floor((new Date() - new Date(localUserData.birthDate)) / 31557600000) : targetUser.age || null,
        standardFields: targetUser.userData?.standardFields,
        userData: {
            ...(targetUser.userData || {}),
            ...localUserData,
            profileBlocks: blocks,
            visibilitySettings,
            isVisible,
            isFeaturedOnHomepage
        }
    };

    return (
        <div className="min-h-screen bg-surface-containerLowest pb-16">
            {/* Top Header Card */}
            <div className="bg-surface border-b border-outlineVariant/50 sticky top-0 z-30 backdrop-blur-md bg-surface/90">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/users')}
                            className="w-10 h-10 rounded-xl bg-surface-container hover:bg-surface-containerHigh text-surface-on flex items-center justify-center border border-outlineVariant transition-colors shrink-0"
                            title="Zurück zu Benutzern"
                        >
                            <ArrowLeftIcon sx={{ fontSize: 20 }} />
                        </button>
                        <div>
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="text-title-large font-bold text-surface-on">
                                    {displayName}
                                </h1>
                                <span className={`px-2.5 py-0.5 rounded-full text-label-small font-bold border ${
                                    isVisible
                                        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20'
                                        : 'bg-error/10 text-error border-error/20'
                                }`}>
                                    {isVisible ? 'Öffentlich (Sichtbar)' : 'Deaktiviert (Unsichtbar)'}
                                </span>
                                {isFeaturedOnHomepage && (
                                    <span className="px-2.5 py-0.5 rounded-full text-label-small font-bold bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20">
                                        ⭐ Startseite
                                    </span>
                                )}
                                <span className="hidden sm:inline-flex px-2 py-0.5 rounded-md text-label-small font-semibold bg-surface-containerHigh text-surface-onVariant">
                                    Admin-Modus
                                </span>
                            </div>
                            <p className="text-body-small text-surface-onVariant">
                                Team-Profil & Sichtbarkeit auf der Website verwalten
                            </p>
                        </div>
                    </div>

                    {/* Header Action Buttons */}
                    <div className="flex flex-wrap items-center gap-2">
                        {publicProfileUrl && isVisible && (
                            <a
                                href={publicProfileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="px-4 py-2 bg-surface-container hover:bg-surface-containerHigh text-surface-on rounded-xl border border-outlineVariant text-label-medium font-bold flex items-center gap-2 transition-colors"
                            >
                                <ExternalLinkIcon size={16} />
                                <span>Webansicht</span>
                            </a>
                        )}

                        <Link
                            to={`/users/${targetUser._id || id}`}
                            className="px-4 py-2 bg-surface-container hover:bg-surface-containerHigh text-surface-on rounded-xl border border-outlineVariant text-label-medium font-bold flex items-center gap-2 transition-colors"
                        >
                            <PersonIcon size={16} />
                            <span>Stammdaten</span>
                        </Link>

                        <button
                            onClick={handleSubmit}
                            disabled={saving}
                            className="md3-btn-filled px-5 py-2 rounded-xl text-label-medium font-bold flex items-center gap-2 shadow-elevation-1 hover:shadow-elevation-2 transition-all disabled:opacity-50"
                        >
                            {saving ? (
                                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            ) : (
                                <SaveIcon size={18} />
                            )}
                            <span>Speichern</span>
                        </button>
                    </div>
                </div>

                {/* Segmented Navigation Tabs */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-2 border-t border-outlineVariant/30 pt-2">
                    <button
                        onClick={() => setActiveTab('preview')}
                        className={`px-4 py-2.5 rounded-t-xl text-label-medium font-bold flex items-center gap-2 border-b-2 transition-all ${
                            activeTab === 'preview'
                                ? 'border-primary text-primary bg-primary/5'
                                : 'border-transparent text-surface-onVariant hover:text-surface-on hover:bg-surface-container'
                        }`}
                    >
                        <VisibilityIcon size={18} />
                        <span>Live-Vorschau & Sichtbarkeit</span>
                    </button>

                    <button
                        onClick={() => setActiveTab('blocks')}
                        className={`px-4 py-2.5 rounded-t-xl text-label-medium font-bold flex items-center gap-2 border-b-2 transition-all ${
                            activeTab === 'blocks'
                                ? 'border-primary text-primary bg-primary/5'
                                : 'border-transparent text-surface-onVariant hover:text-surface-on hover:bg-surface-container'
                        }`}
                    >
                        <ViewQuiltIcon size={18} />
                        <span>Zusatz-Inhalte ({blocks.length})</span>
                    </button>

                    <button
                        onClick={() => setActiveTab('settings')}
                        className={`px-4 py-2.5 rounded-t-xl text-label-medium font-bold flex items-center gap-2 border-b-2 transition-all ${
                            activeTab === 'settings'
                                ? 'border-primary text-primary bg-primary/5'
                                : 'border-transparent text-surface-onVariant hover:text-surface-on hover:bg-surface-container'
                        }`}
                    >
                        <SettingsIcon size={18} />
                        <span>Einstellungen & Toggles</span>
                    </button>
                </div>
            </div>

            {/* Notification message */}
            {message.text && (
                <div className="max-w-4xl mx-auto mt-6 px-4">
                    <div className={`p-4 rounded-2xl flex items-center gap-3 border shadow-xs animate-scale-in ${
                        message.type === 'success'
                            ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 border-emerald-500/20'
                            : message.type === 'info'
                            ? 'bg-primary-container text-primary-onContainer border-primary/20'
                            : 'bg-error-container text-error-on-container border-error/20'
                    }`}>
                        {message.type === 'success' ? <CheckCircleIcon size={20} /> : <AlertCircleIcon size={20} />}
                        <span className="text-body-medium font-semibold">{message.text}</span>
                    </div>
                </div>
            )}

            {/* Content Container */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-6">
                {/* TAB 1: PREVIEW & INLINE EDITING */}
                {activeTab === 'preview' && (
                    <div className="space-y-6">
                        {/* Quick Hint Bar with Visibility Switch */}
                        <div className="bg-surface-container p-4 rounded-2xl border border-outlineVariant flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-primary/10 text-primary">
                                    <VisibilityIcon size={20} />
                                </div>
                                <div>
                                    <p className="text-body-medium font-bold text-surface-on">Interaktive Vorschau & Feldbearbeitung</p>
                                    <p className="text-body-small text-surface-onVariant">
                                        Klicke auf Stift-Symbole zum Bearbeiten oder auf Augen-Icons zum Ein-/Ausblenden einzelner Felder.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 shrink-0 bg-surface-containerHigh/50 px-4 py-2 rounded-xl border border-outlineVariant/50">
                                <span className="text-label-small font-bold text-surface-onVariant uppercase tracking-wider">
                                    Profil-Status:
                                </span>
                                <div className="flex items-center gap-2">
                                    <span className={`text-label-medium font-bold ${isVisible ? 'text-emerald-600 dark:text-emerald-400' : 'text-error'}`}>
                                        {isVisible ? 'Aktiv / Sichtbar' : 'Deaktiviert'}
                                    </span>
                                    <MD3Switch checked={isVisible} onChange={setIsVisible} />
                                </div>
                            </div>
                        </div>

                        {/* Inline Editor Popover if active */}
                        {editingField && (
                            <div className="bg-surface-containerHighest border-2 border-primary p-6 rounded-3xl shadow-elevation-3 animate-scale-in max-w-xl mx-auto">
                                <InlineFieldEditor
                                    {...editingField}
                                    onSave={handleSaveInline}
                                    onCancel={() => setEditingField(null)}
                                />
                            </div>
                        )}

                        {/* High Fidelity Member View */}
                        <div className="bg-surface-containerLow rounded-3xl border border-outlineVariant p-4 sm:p-8 shadow-xs">
                            <TeamMemberView
                                member={previewData}
                                isEditor={true}
                                visibilitySettings={visibilitySettings}
                                onToggleVisibility={handleToggleVisibility}
                                onEdit={handleEditField}
                            />
                        </div>
                    </div>
                )}

                {/* TAB 2: OPTIONAL BLOCKS */}
                {activeTab === 'blocks' && (
                    <div className="space-y-6">
                        <div className="bg-surface-container p-6 rounded-3xl border border-outlineVariant">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-2xl bg-secondary/10 text-secondary flex items-center justify-center">
                                    <ViewQuiltIcon size={22} />
                                </div>
                                <div>
                                    <h2 className="text-title-large font-bold text-surface-on">Zusätzliche Inhaltsblöcke</h2>
                                    <p className="text-body-small text-surface-onVariant">
                                        Füge Zitate, Zeitleisten, Statistiken oder Fotogalerien zum Profil hinzu.
                                    </p>
                                </div>
                            </div>

                            <TeamProfileEditor
                                teamMemberId={targetUser._id || id}
                                blocks={blocks}
                                setBlocks={setBlocks}
                                hideSaveButton={true}
                            />
                        </div>
                    </div>
                )}

                {/* TAB 3: SETTINGS & VISIBILITY */}
                {activeTab === 'settings' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Visibility Card */}
                        <div className="bg-surface-container p-6 rounded-3xl border border-outlineVariant flex flex-col justify-between">
                            <div>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                                        <GlobeIcon size={22} />
                                    </div>
                                    <div>
                                        <h3 className="text-title-medium font-bold text-surface-on">Öffentliche Team-Seite</h3>
                                        <p className="text-body-small text-surface-onVariant">Profil auf der Website aktivieren oder deaktivieren</p>
                                    </div>
                                </div>
                                <p className="text-body-medium text-surface-onVariant mb-6">
                                    Wenn diese Option aktiviert ist, erscheint das Profil in der Team-Übersicht und auf der individuellen Steckbriefseite. Wenn deaktiviert, ist das Profil unsichtbar.
                                </p>
                            </div>
                            <div className="flex items-center justify-between pt-4 border-t border-outlineVariant/50">
                                <div>
                                    <span className="font-bold text-surface-on block">Profil aktivieren (Sichtbar)</span>
                                    <span className="text-body-small text-surface-onVariant">
                                        {isVisible ? 'Profil ist auf der Website öffentlich sichtbar' : 'Profil ist ausgeblendet und nicht abrufbar'}
                                    </span>
                                </div>
                                <MD3Switch checked={isVisible} onChange={setIsVisible} />
                            </div>
                        </div>

                        {/* Homepage Feature Card */}
                        <div className="bg-surface-container p-6 rounded-3xl border border-outlineVariant flex flex-col justify-between">
                            <div>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                                        <HomeIcon size={22} />
                                    </div>
                                    <div>
                                        <h3 className="text-title-medium font-bold text-surface-on">Auf Startseite hervorheben</h3>
                                        <p className="text-body-small text-surface-onVariant">Erscheinung in der Team-Sektion der Homepage</p>
                                    </div>
                                </div>
                                <p className="text-body-medium text-surface-onVariant mb-6">
                                    Zeigt das Profil direkt in der Team-Vorschau auf der Homepage an, um Besuchern die Gruppenleiter vorzustellen.
                                </p>
                            </div>
                            <div className="flex items-center justify-between pt-4 border-t border-outlineVariant/50">
                                <div>
                                    <span className="font-bold text-surface-on block">Auf Startseite anzeigen</span>
                                    <span className="text-body-small text-surface-onVariant">
                                        {isFeaturedOnHomepage ? 'Wird in der Startseiten-Vorschau angezeigt' : 'Nicht auf der Startseite hervorgehoben'}
                                    </span>
                                </div>
                                <MD3Switch checked={isFeaturedOnHomepage} onChange={setIsFeaturedOnHomepage} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditableTeamPage;
